/**
 * Silent session refresh for cookie-credentialed browser callers (HUB-104).
 *
 * The hub's `tf_sso` cookie carries the access token and dies with it
 * (Max-Age = access-token TTL). The `tf_refresh` cookie outlives it, and
 * `POST <hub>/auth/refresh` with the cookie alone re-mints both. So a 401 is,
 * for a browser that signed in through the hub, usually "the access cookie
 * expired" — refresh once and retry, and only surface the 401 when the
 * refresh itself is refused (refresh token expired/revoked → real sign-in).
 *
 * Shared by `HubClient` (`autoRefresh`) and the Copilot widget (`hubBaseUrl`).
 */
/**
 * Returns a `refresh()` that POSTs `<hub>/auth/refresh` once per settlement,
 * deduping concurrent callers onto the same in-flight request. Refresh tokens
 * are single-use with reuse detection: two parallel rotations would burn the
 * whole family and sign the user out, so the dedupe is load-bearing.
 *
 * Resolves `true` iff the hub rotated the session (2xx). Never throws —
 * a refused refresh is the normal "refresh token expired" outcome, which the
 * caller surfaces by letting its original 401 propagate.
 */
export function createSessionRefresher(options) {
    const url = `${options.hubBaseUrl.replace(/\/+$/, '')}/auth/refresh`;
    const fetchImpl = options.fetch ?? fetch;
    let inFlight = null;
    return () => {
        if (inFlight)
            return inFlight;
        const promise = (async () => {
            try {
                // Empty `{}` body — the route reads the tf_refresh cookie. The body
                // keeps Fastify's schema validator happy: the top-level body is an
                // object, and "no body" is rejected as `must be object`.
                const init = {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    body: '{}',
                };
                if (options.credentials)
                    init.credentials = options.credentials;
                const res = await fetchImpl(url, init);
                return res.ok;
            }
            catch {
                return false;
            }
        })();
        inFlight = promise;
        void promise.finally(() => {
            if (inFlight === promise)
                inFlight = null;
        });
        return promise;
    };
}
/**
 * Wraps a `fetch`: on a 401, runs `refresh()` and — when it succeeds — replays
 * the same request once. The hub rotated the cookies on the refresh response,
 * so the replay picks them up automatically. A one-shot `ReadableStream` body
 * cannot be replayed and is passed through untouched; strings, `FormData`,
 * `Blob` and buffers are reusable.
 */
export function withSessionRefresh(fetchImpl, refresh) {
    return async (input, init) => {
        const res = await fetchImpl(input, init);
        if (res.status !== 401 || init?.body instanceof ReadableStream)
            return res;
        if (!(await refresh()))
            return res;
        return fetchImpl(input, init);
    };
}
/**
 * The browser-facing composition: a `fetch` that silently refreshes when
 * `options` names a hub, and the plain global `fetch` when it does not (a
 * track that authenticates with an explicit bearer JWT has no cookie to
 * refresh). `credentials: 'include'` so the cookies flow when the hub is on
 * a sibling origin (`api.tf.com` from `estimates.tf.com`); harmless on
 * same-origin.
 */
export function createSessionFetch(options) {
    const passthrough = (input, init) => fetch(input, init);
    if (!options)
        return passthrough;
    return withSessionRefresh(passthrough, createSessionRefresher({ hubBaseUrl: options.hubBaseUrl, credentials: 'include' }));
}
//# sourceMappingURL=session-refresh.js.map